'use client';

import { faDesktop, faFlaskVial } from '@fortawesome/pro-light-svg-icons';
import { DataTestIdEnum } from '@medprev-web-app/datatestid';

import { Typography } from '@/components/ui/typography/typography.component';
import { ContentContainer } from '@/components/ui/content-container.component';
import {
  FormNotifyMe,
  NotifyHeaderContent,
} from '@/app/(main)/(pages)/_components/face-to-face-service-section/form-notify-me.component';
import { PersonaMediaCard } from '@/app/(main)/(pages)/_components/face-to-face-service-section/persona-media-card.component';
import { buildNotifyMeLeadPayload } from '@/app/(main)/(pages)/_components/face-to-face-service-section/notify-me-lead.util';
import { getAnalyticsPlugin } from '@/app/(main)/_components/logger/analytics/get-analytics-plugin';
import { AnalyticsEnum } from '@/types/enums/analytics/analytics.enum';
import { isLaboratorialSearch, isTelehealthSearch } from '@/app/_utils/is-procedure-search.util';
import { SearchType } from '@/types/most-search-type';

interface SearchScheduleEmptyStateProps {
  procedureName?: string;
  searchType: string;
  procedureCount: number;
  showNotifyForm: boolean;
}

const laboratorialNotifyHeader: NotifyHeaderContent = {
  icon: faFlaskVial,
  title: 'Exames',
  subtitle: 'Seja notificado quando estiver disponível na sua cidade.',
};

const telehealthNotifyHeader: NotifyHeaderContent = {
  icon: faDesktop,
  title: 'Teleconsulta',
  subtitle: 'Seja notificado quando esta especialidade estiver disponível.',
};

function getNotifyHeader(type: SearchType): NotifyHeaderContent | undefined {
  if (isLaboratorialSearch(type)) {
    return laboratorialNotifyHeader;
  }

  if (isTelehealthSearch(type)) {
    return telehealthNotifyHeader;
  }

  return undefined;
}

function getEmptyStateMessage(type: SearchType, procedureCount: number): string {
  if (isLaboratorialSearch(type)) {
    return procedureCount > 1
      ? 'Estes exames não estão disponíveis para compra pelo site em sua cidade.'
      : 'Este exame não está disponível para compra pelo site em sua cidade.';
  }

  if (isTelehealthSearch(type)) {
    return 'Esta especialidade não está disponível para teleconsulta.';
  }

  return 'Este procedimento não está disponível para compra pelo site em sua cidade.';
}

export function SearchScheduleEmptyState({
  procedureName,
  searchType,
  procedureCount,
  showNotifyForm,
}: SearchScheduleEmptyStateProps) {
  const analytics = getAnalyticsPlugin();
  const type = searchType as SearchType;

  const message = getEmptyStateMessage(type, procedureCount);

  return (
    <section
      data-testid={DataTestIdEnum.SEARCH_PAGE_EMPTY_STATE}
      className="flex w-full flex-col items-center py-8"
    >
      <ContentContainer className="md:px-0 md:mx-auto md:max-w-[576px] lg:max-w-3xl flex flex-col gap-6 items-center">
        <Typography className="text-lg leading-6 text-center">
          {message}
        </Typography>

        {showNotifyForm && (
          <div className="w-full xl:grid xl:grid-cols-2 xl:gap-6 xl:items-center">
            <FormNotifyMe
              className="w-full"
              headerLayout="stacked"
              cardStyle="always"
              notifyHeader={getNotifyHeader(type)}
              onSubmitCallback={(data) => {
                analytics.track(
                  AnalyticsEnum.LEAD_SEARCH_NOTIFY_ME,
                  buildNotifyMeLeadPayload(data, { procedimento: procedureName, tipo_procedimento: type }),
                );
              }}
            />

            <PersonaMediaCard
              className="hidden xl:flex h-[24.5rem]"
              imageClassName="h-[22rem] w-fit"
            />
          </div>
        )}
      </ContentContainer>
    </section>
  );
}
